import React, { FunctionComponent } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@hxnova/icons";
import { IconButton, ThemeProvider } from "@hxnova/react-components";
import { NovaTheme } from "@hxnova/themes";

import "../../../css/shared/page_toast.css";

export type PageToastTone = "neutral" | "success" | "error";

export type Props = {
  message: string
  iconName?: string
  tone?: PageToastTone
  onDismiss: () => void
};

/**
 * The Nova toast itself: fixed bottom-right, above everything, dismissable. Features do not render
 * this directly -- {@link ../../services/component_helpers/component_effects/use_toast} owns the one
 * instance and the timer, so a page cannot end up with two of them stacked.
 *
 * It carries its own Nova ThemeProvider because it portals to document.body, outside whatever theme
 * the calling page sits in.
 */
const PageToast: FunctionComponent<Props> = ({ message, iconName = "check_circle", tone = "neutral", onDismiss }) => {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
      <div className="PageToastContainer" role="status" aria-live="polite">
        <div className={`PageToast PageToast--${tone}`}>
          <Icon name={iconName} family="material" filled size={24}/>
          <span className="PageToast-message">{message}</span>
          <IconButton variant="standard" aria-label="Dismiss" onClick={onDismiss} className="PageToast-closeBtn">
            <Icon name="close" family="material" size={20}/>
          </IconButton>
        </div>
      </div>
    </ThemeProvider>,
    document.body
  );
};

export default PageToast;
